import { Button } from '@material-ui/core';
import { cloneDeep, debounce } from 'lodash-es';
import moment from 'moment-timezone';
import React from 'react';
import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer';

import {
  cleanCommaSeparatedString,
  ReportingEventTypes,
  track,
} from '~/src/dash/analytics/analytics';
import { getToken } from '~/src/dash/api/auth/auth';
import { getSitesPromise, simplifiedChannelsPreloads } from '~/src/dash/api/channels';
import {
  getLastUpdateTimestampBuyersAPS,
  getLastUpdateTimestampByAuctions,
  getLastUpdateTimestampByAuctionsFillRate,
  getLastUpdateTimestampByBrandSafetyTrackerReport,
  getLastUpdateTimestampByBuyers,
  getLastUpdateTimestampByCampaign,
  getLastUpdateTimestampByContextualSegments,
  getLastUpdateTimestampByDeals,
  getLastUpdateTimestampByEleaAIEnrichment,
  getLastUpdateTimestampByEleaAIScanAll,
  getLastUpdateTimestampByInventoryRoutes,
  getLastUpdateTimestampByPodHourly,
  getLastUpdateTimestampByPublicaDeals,
  getLastUpdateTimestampBySegments,
  getLastUpdateTimestampByShareOfVoice,
  getLastUpdateTimestampBySSAI,
  getLastUpdateTimestampByUniqueUserReport,
  getPublisher,
  IAnalytics,
  IPublisher,
  ISite,
  queryAnalyticsWithSizeV2,
  REPORTTYPE_ADVERTISER_RULES,
  REPORTTYPE_BUYERS_APS,
  REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS,
  REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER,
  REPORTTYPE_HEADERBIDDER_BYAUCTIONS,
  REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE,
  REPORTTYPE_HEADERBIDDER_BYBUYERS,
  REPORTTYPE_HEADERBIDDER_CAMPAIGN,
  REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS,
  REPORTTYPE_HEADERBIDDER_DEALS,
  REPORTTYPE_HEADERBIDDER_DESTINATIONS,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL,
  REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES,
  REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS,
  REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT,
  REPORTTYPE_POD_HOURLY,
  REPORTTYPE_SHARE_OF_VOICE,
  REPORTTYPE_STREAM,
} from '~/src/dash/api/console-api';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import {
  getFirstAndThirdPartyIasData,
  IIASFirstAndThirdPartyRequestBody,
  isIASReport,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { DEMO_ACCOUNTS } from '~/src/dash/globals/constants';
import { IStore } from '~/src/dash/reducers/store';
import { getApiDomain } from '~/src/dash/resellers/utils/importResellerConfig';
import { IHistoryProps, ISessionStateProps, IUserStateProps } from '~/src/dash/types/props';
import { formatDateEndDay, formatDateStartDay, parseDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import {
  getReportGraphID,
  mapBidderLabel,
  mapBidderType,
  mapBidErrorCode,
  mapBidWonRejectReason,
  mapCampaignNoFillCode,
  mapChannelLabel,
  mapCountryGroup,
  mapCreativeReviewStatus,
  mapDestinationType,
  mapDeviceType,
  mapEndpointErrorCode,
  mapInventoryRouteCondition,
  mapInventoryRouteErrorCode,
  mapPhiloFallbackType,
  mapStreamErrorCode,
  mapTargetingDevice,
  mapVastErrorCode,
} from '~/src/dash/utils/graph';
import { IParams, parseSearch } from '~/src/dash/utils/location';
import { whiteColor } from '~/src/dash/utils/material-dashboard-react';
import { filterSitesByActive } from '~/src/dash/utils/models';
import {
  allowedEmptyIPermissionCheck,
  IPermissionCheck,
  IS_AUTHORIZED,
} from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { RowButton } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { ReportTable } from '~/src/dash/views/report-table-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps extends IHistoryProps, IUserStateProps, ISessionStateProps {}

interface IState {
  loading: boolean;
  countAnalytics: number;
  countLastUpdate: number;

  lastUpdate?: number;
  data: IAnalytics[];

  query: IParams;
  dateStart: moment.Moment;
  dateEnd: moment.Moment;
  sites: Map<string, string>;
  isPresetAggregateSelected: boolean;

  readAuthorization: IPermissionCheck;
  // for mixPanel
  startedTime: number;
  exchangeRate: number;
  currencyDisplayed: ECurrencyType;
  snackbarMessage: string;
  snackbarError: boolean;
  rawQuery: string;
}

export const ReportFullScreenView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = (() => {
      const query = parseSearch(this.props.location.search);
      return {
        loading: true,
        countAnalytics: 0,
        countLastUpdate: 0,
        lastUpdate: undefined,
        data: [] as IAnalytics[],

        query,
        dateStart: parseDate(query.start_date),
        dateEnd: parseDate(query.end_date),
        timezone: query.timezone,
        sites: new Map<string, string>(),
        readAuthorization: allowedEmptyIPermissionCheck,
        isPresetAggregateSelected: false,
        startedTime: moment().unix(),
        exchangeRate: 1,
        currencyDisplayed: ECurrencyType.CURRENCY_TYPE_USD,
        snackbarMessage: '',
        snackbarError: false,
        rawQuery: '',
      };
    })();

    _refreshData = debounce((countAnalytics: number) => {
      if (countAnalytics !== this.state.countAnalytics) {
        return;
      }
      this.refreshLastUpdate();
      const getDataQuery = this._getDataQuery();
      getDataQuery.then(
        (resp) => {
          let data = resp[0];
          const size = resp[1];
          const rawQuery = resp[2];
          if (countAnalytics !== this.state.countAnalytics) {
            return;
          }
          data = mapBidErrorCode(data);
          data = mapInventoryRouteErrorCode(data);
          data = mapDestinationType(data);
          data = mapInventoryRouteCondition(data);
          data = mapEndpointErrorCode(data);
          data = mapVastErrorCode(data);
          data = mapStreamErrorCode(data);
          data = mapCampaignNoFillCode(data);
          data = mapBidWonRejectReason(data);
          data = mapTargetingDevice(data);
          data = mapBidderType(data);
          data = mapBidderLabel(data);
          data = mapDeviceType(data);
          data = mapCountryGroup(data);
          data = mapChannelLabel(data);
          data = mapPhiloFallbackType(data);
          data = mapCreativeReviewStatus(data);

          if (this.state.query.groupbyoptions.indexOf('hour') > -1) {
            data.forEach((dataEntry: { [key: string]: any }) => {
              dataEntry.Hour = dataEntry.Date;
            });
          }
          if (this.state.query.groupbyoptions.indexOf('day') > -1) {
            data.forEach((dataEntry: { [key: string]: any }) => {
              dataEntry.Day = dataEntry.Date;
            });
          }
          if (this.state.query.groupbyoptions.indexOf('month') > -1) {
            data.forEach((dataEntry: { [key: string]: any }) => {
              dataEntry.Month = dataEntry.Date;
            });
          }
          if (this.state.query.groupbyoptions.indexOf('week_of_year') > -1) {
            data.forEach((dataEntry: { [key: string]: any }) => {
              dataEntry.WeekOfYear = dataEntry.Date;
            });
          }
          this.trackEventLoadTime(moment().unix(), size);
          this.setState({
            loading: false,
            data,
            rawQuery,
          });
        },
        (err) => {
          if (countAnalytics !== this.state.countAnalytics) {
            return;
          }
          if (err.message) {
            this.setState({
              snackbarMessage: err.message,
              snackbarError: true,
              loading: false,
            });
          } else {
            this.setState({
              loading: false,
            });
          }
        },
      );
    }, 200);

    _refreshLastUpdate = debounce((countLastUpdate: number) => {
      if (countLastUpdate !== this.state.countLastUpdate) {
        return;
      }
      if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_BYAUCTIONS) {
        getLastUpdateTimestampByAuctions({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestampByAuctions: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestampByAuctions,
            });
          },
          () => {
            console.error('failed to get last update timestamp by auctions');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_BYBUYERS) {
        getLastUpdateTimestampByBuyers({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestampByBuyers: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestampByBuyers,
            });
          },
          () => {
            console.error('failed to get last update timestamp by buyers');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE) {
        getLastUpdateTimestampByAuctionsFillRate({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestampByAuctionsFillRate: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestampByAuctionsFillRate,
            });
          },
          () => {
            console.error('failed to get last update timestamp by auctions fill rate');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_CAMPAIGN) {
        getLastUpdateTimestampByCampaign({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestampByCampaign: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestampByCampaign,
            });
          },
          () => {
            console.error('failed to get last update timestamp by auctions');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS) {
        getLastUpdateTimestampBySegments({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestampBySegments: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestampBySegments,
            });
          },
          () => {
            console.error('failed to get last update timestamp by auctions');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_STREAM) {
        getLastUpdateTimestampBySSAI({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by ssai');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT) {
        getLastUpdateTimestampByUniqueUserReport({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by unique user report');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER) {
        getLastUpdateTimestampByBrandSafetyTrackerReport({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by brand safety tracker report');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_DEALS) {
        getLastUpdateTimestampByDeals({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by deals report');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS) {
        getLastUpdateTimestampByPublicaDeals({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by publica deals report');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL) {
        getLastUpdateTimestampByEleaAIScanAll({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by EleaAI Scan All report');
          },
        );
      } else if (
        getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT ||
        getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION
      ) {
        getLastUpdateTimestampByEleaAIEnrichment({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by EleaAI Enrichment report');
          },
        );
      } else if (
        getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES ||
        getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_DESTINATIONS
      ) {
        getLastUpdateTimestampByInventoryRoutes({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by inventory routing report');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS) {
        getLastUpdateTimestampByContextualSegments({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by contextual segments report');
          },
        );
      } else if (getReportType(this.state.query) === REPORTTYPE_SHARE_OF_VOICE) {
        getLastUpdateTimestampByShareOfVoice({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by publica deals report');
          },
        );
      } else if (
        [REPORTTYPE_POD_HOURLY, REPORTTYPE_ADVERTISER_RULES].includes(
          getReportType(this.state.query),
        )
      ) {
        getLastUpdateTimestampByPodHourly({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by pod hourly report');
          },
        );
      } else if (REPORTTYPE_BUYERS_APS === getReportType(this.state.query)) {
        getLastUpdateTimestampBuyersAPS({
          query: {
            publisher_id: getPublisherId(this.state.query),
          },
        }).then(
          (data: { LastUpdateTimestamp: number }) => {
            if (countLastUpdate !== this.state.countLastUpdate) {
              return;
            }
            this.setState({
              lastUpdate: data.LastUpdateTimestamp,
            });
          },
          () => {
            console.error('failed to get last update timestamp by buyers aps report');
          },
        );
      }
    }, 200);

    _generateIASQuery = (): IIASFirstAndThirdPartyRequestBody => {
      const reportGraphID = getReportGraphID(getReportType(this.state.query), [], []);
      // parse ias device types
      const DeviceTypes = [] as number[];
      if (this.state.query.devicetypes.length > 0) {
        const deviceTypesNoQuotes = this.state.query.devicetypes.replaceAll(`'`, ``);
        for (const deviceTypes of deviceTypesNoQuotes.split(',')) {
          for (const deviceType of deviceTypes.split('|')) {
            DeviceTypes.push(parseInt(deviceType, 10));
          }
        }
      }
      let groupbyoptions = this.state.query.groupbyoptions;
      for (const option of ['month', 'day']) {
        groupbyoptions = groupbyoptions.replace(option, 'date');
      }
      return {
        Debug: hasRoleSuperUser(this.props.user.user),
        GraphID: `graph${reportGraphID}.sql`,
        ReportName: this.state.query.report_name,
        TeamID: '15661',
        StartDate: formatDateStartDay(this.state.dateStart),
        EndDate: formatDateEndDay(this.state.dateEnd),
        GroupingNumber: parseInt(this.state.query.grouping, 10),
        Timezone: this.state.query.timezone,
        GroupBys: cleanCommaSeparatedString(groupbyoptions),
        Metrics: cleanCommaSeparatedString(this.state.query.metrics),
        PublicaChannelIDs: cleanCommaSeparatedString(this.state.query.site_ids).map((id) =>
          Number(id),
        ),
        PublicaBidderIDs: cleanCommaSeparatedString(this.state.query.bidder_ids).map((id) =>
          Number(id),
        ),
        IsBidderIDsIncluded: parseInt(this.state.query.is_bidder_ids_included, 10),
        PublicaBidderGroupIDs: cleanCommaSeparatedString(this.state.query.bidder_group_ids).map(
          (id) => Number(id),
        ),
        PublicaBidderTypes: cleanCommaSeparatedString(this.state.query.biddercodes),
        ReportLimit: Number(this.state.query.report_limit),
        AnIDs: [Number(this.state.query.an_id)],
        OrderIDs: [], // blank until we add ability for filter dropdowns
        CreativeIDs: [], // blank until we add ability for filter dropdowns
        DeviceTypes,
        LineItemIDs: [], // blank until we add ability for filter dropdowns
      } as IIASFirstAndThirdPartyRequestBody;
    };

    _getDataQuery = (): Promise<[IAnalytics[], number, string]> => {
      // If the report is an IAS Report we do use a different query/route
      if (isIASReport(getReportType(this.state.query))) {
        return getFirstAndThirdPartyIasData(this._generateIASQuery(), {
          publisher_id: this.state.query.publisher_id,
        });
      } else {
        return queryAnalyticsWithSizeV2({
          query: {
            ...this.state.query,
            start_date: formatDateStartDay(this.state.dateStart),
            end_date: formatDateEndDay(this.state.dateEnd),
          },
        });
      }
    };

    componentWillMount() {
      // check if a preset aggregate is selected in the group bys
      if (
        getGroupBy(this.state.query).filter((groupBy: string) =>
          groupBy.startsWith('preset_aggregate'),
        ).length > 0
      ) {
        // a preset aggregate have been selected
        this.state.isPresetAggregateSelected = true; // no need for setState since the components aren't loaded yet
      }
    }

    componentDidMount() {
      // because this component works different in app-view, the props publisher is actually the user
      // so we need to manually call for the publisher here to get the exchangeRate and currency symbol
      getPublisher({
        query: {
          publisher_id: getPublisherId(this.state.query),
        },
      }).then((publisher: IPublisher) => {
        const readAuthorization = IS_AUTHORIZED(['read_report'], this.props.user.user);
        this.setState({
          readAuthorization,
          exchangeRate: this.state.query.exchangerate
            ? this.state.query.exchangerate
            : publisher.ExchangeRate,
          currencyDisplayed: this.state.query.currency
            ? Number(this.state.query.currency)
            : publisher.CurrencyDisplayed,
        });

        if (!readAuthorization.isAuthorized) {
          return;
        }

        this.refreshData();

        this.createSitesIndex();
      });
    }

    trackEventLoadTime = (loadedTime: number, size: number) => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      const urlParams = new URLSearchParams(this.props.location.search);
      const eventUUID = urlParams.get('event_uuid');
      track(
        'Report Preview - Preview Loaded',
        {
          is_super_user,
          load_duration_sec: loadedTime - this.state.startedTime,
          report_size: size,
          // ids
          event_uuid: eventUUID,
          report_id: this.state.query.report_id,
          report_name: this.state.query.report_name,
          report_type: this.state.query.report_type_string,
          subject: this.state.query.subject,
          graph_id: this.state.query.graph_id,
          // group bys and metrics
          metrics:
            this.state.query.metrics === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.metrics),
          groupbyoptions:
            this.state.query.groupbyoptions === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.groupbyoptions),
          // date info
          start_date: this.state.query.start_date,
          end_date: this.state.query.end_date,
          report_duration_days: this.state.query.report_duration_days,
          // ids
          channel_group_ids:
            this.state.query.channel_group_ids === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.channel_group_ids),
          site_ids:
            this.state.query.site_ids === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.site_ids),
          bidder_ids:
            this.state.query.bidder_ids === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.bidder_ids),
          campaign_ids:
            this.state.query.campaign_ids === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.campaign_ids),
          segment_ids:
            this.state.query.segment_ids === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.segment_ids),
          network_ids:
            this.state.query.network_ids === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.network_ids),
          // other filters
          stream_types:
            this.state.query.stream_types === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.stream_types),
          devicetypes:
            this.state.query.devicetypes === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.devicetypes),
          biddercodes:
            this.state.query.biddercodes === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.biddercodes),
          countries:
            this.state.query.countries === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.countries),
          dmas:
            this.state.query.dmas === '' ? [] : cleanCommaSeparatedString(this.state.query.dmas),
          states:
            this.state.query.states === ''
              ? []
              : cleanCommaSeparatedString(this.state.query.states),
          report_limit: this.state.query.report_limit,
          report_order_by: this.state.query.report_order_by,
          timezone: this.state.query.timezone,
        },
        this.props.user.user,
      );
    };

    trackEvent = (name: ReportingEventTypes) => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      const urlParams = new URLSearchParams(this.props.location.search);
      const eventUUID = urlParams.get('event_uuid');
      track(
        name,
        {
          is_super_user,
          event_uuid: eventUUID,
        },
        this.props.user.user,
      );
    };

    refreshData = () => {
      const countAnalytics = this.state.countAnalytics + 1;
      this.setState(
        {
          countAnalytics,
          loading: true,
          startedTime: moment().unix(),
        },
        () => this._refreshData(countAnalytics),
      );
    };

    refreshLastUpdate = () => {
      const countLastUpdate = this.state.countLastUpdate + 1;
      this.setState({
        countLastUpdate,
      });
      this._refreshLastUpdate(countLastUpdate);
    };

    handleDateChange = ({ start, end }: IDateRangeChangeEvent) => {
      this.setState(
        {
          dateStart: start,
          dateEnd: end,
        },
        () => {
          this.trackEvent('Report Preview - Calendar');
          this.refreshData();
        },
      );
    };

    createSitesIndex() {
      const out = new Map<string, string>();
      getSitesPromise({
        publisherIDs: [`${getPublisherId(this.state.query)}`],
        publisherID: `${getPublisherId(this.state.query)}`,
        preloads: [
          simplifiedChannelsPreloads.CHANNEL_LABELS,
          simplifiedChannelsPreloads.SUB_SITES,
          simplifiedChannelsPreloads.CHANNEL_PERFORMANCE_SORT_BY_OPTIONS,
        ],
      }).then((sites) => {
        sites = filterSitesByActive(sites);
        sites.forEach((site: ISite) => {
          out.set(site.Name, site.ID.toString());
        });
        this.setState({ sites: out });
      });
    }

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render() {
      if (!this.state.readAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={this.state.readAuthorization.missingPermissions}
          />
        );
      }

      return (
        <div style={{ background: whiteColor, height: '100vh', width: '100vw' }}>
          <div style={{ height: '72px', padding: '12px 25px 20px 15px' }}>
            <RowButton
              RowProps={{
                style: {
                  alignItems: 'center',
                },
              }}
              left={
                <div>
                  <div style={{ display: 'inline-block' }}>
                    <DateTimeRangeComponent
                      disabled={this.state.isPresetAggregateSelected} // if a preset aggregate is selected, disable time range
                      timezone={this.state.timezone}
                      start={this.state.dateStart}
                      end={this.state.dateEnd}
                      onChange={this.handleDateChange}
                      disableTime={true}
                      enableSelectAll={true}
                      maxDateExtended={-1}
                      minDate={moment().subtract(2, 'years')}
                    />
                  </div>
                  <span
                    style={{
                      margin: '0 0 0 30px',
                      fontSize: '1rem',
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {/* {!checkUserInfoMasked(user.user) && (
                      <UINoticeTopbar
                        uiNotices={(user.uiNotices || []).filter(
                          (uiNotice: IUINotice) => uiNotice.Active === UINoticeActiveTrue,
                        )}
                        currentPath={currentPath}
                      />
                    )} */}
                    Last Updated:{' '}
                    {this.state.lastUpdate
                      ? `${moment((this.state.lastUpdate || 0) * 1000)
                          .tz(this.state.timezone)
                          .format('MMMM Do YYYY, h:mm:ss a')} [timezone: ${this.state.timezone}]`
                      : 'N/A'}
                  </span>
                </div>
              }
              right={
                <div style={{ paddingTop: '6px', alignItems: 'center' }}>
                  &nbsp;&nbsp;&nbsp;
                  <Button
                    variant='contained'
                    onClick={() => {
                      this.trackEvent('Report Preview - Refresh');
                      this.refreshData();
                    }}
                  >
                    Refresh
                  </Button>
                  &nbsp;&nbsp;&nbsp;
                  <Button
                    variant='contained'
                    download
                    color='primary'
                    href={(() => {
                      const query = cloneDeep(this.state.query);
                      query.ias_body = JSON.stringify(this._generateIASQuery());
                      return getDownloadURL(
                        query,
                        isIASReport(getReportType(this.state.query)),
                        this.state.dateStart,
                        this.state.dateEnd,
                      );
                    })()}
                    onClick={(e) => {
                      if (this.state.query.adomains !== '') {
                        const limit = parseInt(this.state.query.report_limit, 10);
                        if (isNaN(limit) || limit <= 0 || limit > 500000) {
                          this.setState({
                            snackbarMessage:
                              'Request to download report failed. Adomain filter requires a Limit between 1 and 500000.',
                            snackbarError: true,
                          });
                          e.preventDefault();
                          return;
                        }
                      }
                      this.trackEvent('Report Preview - Download Report');
                    }}
                  >
                    Download
                  </Button>
                </div>
              }
            />
          </div>

          {/* {this.state.data.length === 5000 && (
            <div style={{ height: '48px', textAlign: 'center' }}>
              <UINoticeTopbar
                uiNotices={[
                  {
                    Description:
                      'Only 5000 rows of data is presented. For full report, please download or schedule an email.',
                  },
                ]}
                currentPath={currentPath}
                styleOverride={{ position: 'static', display: 'inline-block' }}
              />
            </div>
          )} */}

          <div style={{ height: 'calc(100vh - 72px)', width: '100vw', position: 'relative' }}>
            <AutoSizer>
              {({ width, height }: { width: number; height: number }) => {
                if (this.state.loading) {
                  return <LoadingView active={true} />;
                }
                if (this.state.rawQuery) {
                  return (
                    <div style={{ width: '90%', marginLeft: 20 }}>
                      <div style={{ whiteSpace: 'pre' }}>{this.state.rawQuery}</div>
                    </div>
                  );
                }
                return (
                  <ReportTable
                    isPresetAggregateSelected={this.state.isPresetAggregateSelected}
                    loading={this.state.loading}
                    data={this.state.data}
                    groupBy={getGroupBy(this.state.query)}
                    metrics={getMetrics(this.state.query)}
                    height={height}
                    width={width}
                    obfuscateSiteTitle={DEMO_ACCOUNTS.includes(this.props.user.user.Username)}
                    sites={this.state.sites}
                    publisherID={getPublisherId(this.state.query).toString()}
                    currencyDisplayed={this.state.currencyDisplayed}
                    exchangeRate={this.state.exchangeRate}
                    reportType={getReportType(this.state.query)}
                    user={this.props.user.user}
                  />
                );
              }}
            </AutoSizer>
          </div>

          <SnackbarView
            open={!!this.state.snackbarMessage}
            error={!!this.state.snackbarError}
            onClose={this.snackbarClose}
          >
            {this.state.snackbarMessage}
          </SnackbarView>
        </div>
      );
    }
  },
);

function getReportType(query: IParams): string {
  if (query.report_type) {
    return '' + query.report_type;
  }
  return '0';
}

function cleanValue(v: string) {
  return v.replace(/['" ]/g, '');
}

function getGroupBy(query: IParams): string[] {
  if (query.groupbyoptions) {
    return query.groupbyoptions.split(',').map(cleanValue);
  }
  return [];
}

function getMetrics(query: IParams): string[] {
  if (query.metrics) {
    return query.metrics.split(',').map(cleanValue);
  }
  return [];
}

function getPublisherId(query: IParams): number {
  if (query.publisher_id) {
    return query.publisher_id;
  }
  return 0;
}

function getDownloadURL(
  query: IParams,
  isIASReport: boolean,
  dateStart: moment.Moment,
  dateEnd: moment.Moment,
): string {
  let downloadUrl = isIASReport
    ? `${getApiDomain()}/v1/settings/download_ias_report?`
    : `${getApiDomain()}/v1/analytics/discovery/download_report?`;

  downloadUrl = downloadUrl.concat('access_token=' + getToken().access_token + '&');
  downloadUrl = downloadUrl.concat(
    getQueryParameters(query, ['includeQuotes', 'start_date', 'end_date']),
  );

  const sDate = formatDateStartDay(dateStart);
  const eDate = formatDateEndDay(dateEnd);

  downloadUrl = downloadUrl.concat('&start_date=' + encodeURIComponent(sDate));
  downloadUrl = downloadUrl.concat('&end_date=' + encodeURIComponent(eDate));

  downloadUrl = downloadUrl.concat('&includeQuotes=false');

  return downloadUrl;
}

function getQueryParameters(query: IParams, ignoreKeys?: string[]): string {
  const ret: string[] = [];
  for (const key of Object.keys(query)) {
    if (ignoreKeys && ignoreKeys.includes(key)) {
      continue;
    }
    const value = query[key];
    ret.push(encodeURIComponent(key) + '=' + encodeURIComponent(value.split("'").join('')));
  }
  return ret.join('&');
}
